import SimplePopup from '../../../../../components/bases/popup/simplePopup.js';
import { GlobalContext } from '../../../../../utils/globalprovider.js';

import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
} from 'react';
import './edit-list-st.css';

import Subtitle from './components/subtitle/subtitle.js';
import Filter from './components/filter/filter.js';

import { reducer, initialState } from './actions/state.js';
import { cloneSubtitle, wait } from '../common/functions.js';
import { useCallbacks } from './actions/callbacks.js';
import Search from './components/search/search.js';
import { useCallbacksSearch } from './actions/search.js';
import { cloneDeep, first } from 'lodash';
import { getLisibility } from '../../../../../utils/checksubtitles.js';

const EditListSt = forwardRef((props, ref) => {
  const context = useContext(GlobalContext);
  const lang = context.language;

  const [forceRedraw, setForceRedraw] = React.useState(false);

  const [state, dispatch] = React.useReducer(reducer, initialState);

  const [displaySubtitles, setDisplaySubtitles] = React.useState([]);
  const [displayFilter, setDisplayFilter] = React.useState(false);

  const [nbWordsInError, setNbWordsInError] = React.useState(0);

  const subtitleRefs = React.useRef([]);

  const searchRef = React.createRef();
  const filterRef = React.createRef();

  const onDispatch = useCallback(
    (value, callback, timeout = 100) => {
      // setTimeout(() => {
      dispatch(value);
      //}, 100);

      if (callback) {
        setTimeout(() => {
          callback();
        }, 100);
      }
    },
    [dispatch]
  );

  const {
    onWheel,
    onHandleChangeScroll,
    onChangePosition,
    onSelectSubtitles,
    onSelectRangeSubtitles,
    onDeleteSelectedSubtitles,
    onSubtitleChecked,
    onSubtitleLineChecked,
    onSetColor,
    onDuplicateSubtitles,
    onUndoOrRedo,
    onSelectColor,
    onClearFilteredSubtitles,
    onSelectAlign,
    onSearchWordOnSubtitles,
    onCutAndPasteSubtitles,
    onRefreshListSubtitlesCanvas,
    getNewScrollValue
  } = useCallbacks(state, props, onDispatch, subtitleRefs);

  const {
    onReplaceAll,
    setPositionOfSearch
  } = useCallbacksSearch(state, props, onDispatch, subtitleRefs);

  useImperativeHandle(ref, () => ({
    get active() {
      return state.active;
    },

    setSubtitles(subtitleNumber, subtitleParameters, subtitles) {
      dispatch({
        type: 'onInit',
        value: {
          subtitles,
          subtitleParameters,
          currentSubtitleId: 0,
          subtitleNumber,
          scroll: {
            value: 0,
            hasChanged: false,
          },
        },
      });
    },

  // ***************************************************************************************
  // getSubtitlesPtr()
  // Récupération pointeur de la transcription corrigée
  // ***************************************************************************************
    getSubtitlesPtr()    
    {
        return cloneDeep(state.subtitles);
    },

    setSubtitlesPtr(subtitles)    
    {
      dispatch({
          type: 'onUpdateSubtitles',
          value: { subtitles },
      });

      setTimeout(() => {
        this.refresh();
      }, 50);
    },

    getSubtitles() {
      return state.subtitles.map(x => {
        const subtitle = cloneSubtitle(x);
        
        subtitle.lisibility = getLisibility(subtitle, state.subtitleParameters);

        return subtitle;
      });
    },

    getComputedSubtitles(stparams) {
      return state.subtitles.map((x) => {
        const subtitle = cloneSubtitle(x);

        subtitle.lisibility = getLisibility(subtitle, stparams);

        return subtitle;
      });
    },

    setParamsST(params) {
      dispatch({ type: 'onChangeParams', value: params });
      setForceRedraw(true);
    },

    refresh() {
      setForceRedraw(true);
    },

    async updateSubtitles(updatedSubtitles, currentSubtitleId = undefined) {
      const subtitles = cloneDeep(updatedSubtitles).map((updatedSubtitle) => {
        const existingSubtitle = state.subtitles.find(
          (subtitle) => subtitle.id === updatedSubtitle.id
        );
        return existingSubtitle
          ? {
              ...existingSubtitle,
              ...updatedSubtitle,
              display: existingSubtitle.display,
              startY: existingSubtitle.startY,
              endY: existingSubtitle.endY,
            }
          : { ...updatedSubtitle };
      });

     /* dispatch({
        type: 'onUpdateSubtitles',
        value: { subtitles },
      });*/

      
    // hack pour OTTO, car apparemment ca ne dispatche pas bien sans timeout
        setTimeout(() => {
          dispatch({
            type: 'onUpdateSubtitles',
            value: { subtitles },
          });
        }, 50);

      if (state.filtered) {
        const subtitlesFiltered = state.subtitlesFiltered.map(
          (existingSubtitle) => {
            const updatedSubtitle = updatedSubtitles.find(
              (subtitle) => subtitle.id === existingSubtitle.id
            );

            return existingSubtitle
              ? {
                  ...existingSubtitle,
                  ...updatedSubtitle,
                  display: existingSubtitle.display,
                  startY: existingSubtitle.startY,
                  endY: existingSubtitle.endY,
                }
              : updatedSubtitle;
          }
        );

        dispatch({
          type: 'onFilteredSubtitles',
          value: { subtitlesFiltered },
        });
      }

      if (currentSubtitleId && currentSubtitleId !== state.currentSubtitleId) {
  
        if (state.active) {
          const currentSubtitle = subtitles.find(
            (x) => x.id === currentSubtitleId
          );
          if (currentSubtitle) {
            props.onMove(currentSubtitle.id, currentSubtitle.in);
          }
        } else {
          dispatch({
            type: 'onUpdateCurrentSubtitleId',
            value: {
              id: currentSubtitleId,
              active: state.active,
            },
          });

          await wait(100);
        }
      }
    },

    setCurrentSubtitle(id, active = true) {
      if (state.currentSubtitleId === id && state.active === active) {
        return;
      }

      const { value: currentScrollValue, hasChanged } = state.scroll;

      // si le scroll n'a pas changé
      if (!hasChanged) {
        const currentIndex = displaySubtitles.findIndex((x) => x.id === id);

        if (currentIndex === -1) {          
          dispatch({
            type: 'onUpdateCurrentSubtitleId',
            value: {
              id,
              active,
            },
          });
          
          return;
        }

        let newScrollValue = getNewScrollValue(currentIndex);

        if (newScrollValue !== currentScrollValue) {
          dispatch({
            type: 'onUpdateCurrentSubtitleIdAndScroll',
            value: {
              id,
              active,
              scrollValue: newScrollValue,
            },
          });

          return;
        }
      }

      dispatch({
        type: 'onUpdateCurrentSubtitleId',
        value: {
          id,
          active,
        },
      });
    },

    clearCurrentSubtitle() {
      dispatch({
        type: 'onUpdateCurrentSubtitleId',
        value: {
          id: null,
          active: false,
        },
      });
    },

    updateSubtitle(updatedSubtitle) {
      const { subtitles } = state;

      const updatedSubtitles = subtitles.map((subtitle) =>
        subtitle.id === updatedSubtitle.id
          ? {
              ...updatedSubtitle,
              display: subtitle.display,
              startY: subtitle.startY,
              endY: subtitle.endY,
            }
          : subtitle
      );

      dispatch({
        type: 'onUpdateSubtitles',
        value: { subtitles: updatedSubtitles },
      });
    },

    scrollToSubtitle(id) {
       const currentIndex = displaySubtitles.findIndex((x) => x.id === id);
       if (currentIndex === -1) {
        return;
       }

       let newScrollValue = getNewScrollValue(currentIndex);

        if (newScrollValue !== state.scroll.value) {
          dispatch({
            type: 'onUpdateCurrentSubtitleIdAndScroll',
            value: {
              id,
              active: state.active,
              scrollValue: newScrollValue,
            },
          });
        }
    },

    async updateSubtitleAfterUndoOrRedo(action, active = true) {

      if (state.showSearch && action.search) {
        onDispatch({
          type: "onSearchUpdate",
          value: {
            ...action.search
          }
        }, () => {
          props.onMove(currentSubtitle.id, currentSubtitle.in);
        });         
        
        return;
      }


      const currentSubtitle = action.subtitle;
      this.updateSubtitle(currentSubtitle);

      this.setCurrentSubtitle(action.subtitle.id, active);

      await wait(100);

      const activeRef = subtitleRefs.current.find(
        (x) => x.subtitle.id === action.subtitle.id
      );
      if (activeRef) {
        activeRef.updateSubtitleAfterUndoOrRedo(action, active);
      }
    },

    setWordsInError(wordsInError) {
      const subtitles = cloneDeep(state.subtitles);

      const wordsSuggested = subtitles
        .flatMap((sub) => sub.lines)
        .flatMap((l) => l.words)
        .filter((x) => x.suggest !== undefined);

      for (const word of wordsSuggested) {
        word.suggest = undefined;
      }

      for (const wordInError of wordsInError) {
        const subtitle = subtitles.find((x) => x.id === wordInError.subtitleId);
        if (!subtitle) {
          continue;
        }

        const line = subtitle.lines[wordInError.indexLine];
        if (line) {
          const word = line.words.find((x) => x.text === wordInError.text);
          if (word) {
            word.suggest = wordInError.suggest;
          }
        }
      }

      setNbWordsInError(wordsInError.length);

      dispatch({
        type: 'onUpdateSubtitles',
        value: { subtitles },
      });
    },

    setWordsInErrorToCurrentSubtitle(wordsInError) {
      const { subtitles, currentSubtitleId } = state;

      const currentSubtitle = subtitles.find((x) => x.id === currentSubtitleId);
      if (!currentSubtitle) {
        return;
      }

      let hasChanges = false;

      const wordsSuggested = currentSubtitle.lines
        .flatMap((l) => l.words)
        .filter((x) => x.suggest !== undefined);

      if (wordsSuggested.length) {
        wordsSuggested.forEach((word) => {
          word.suggest = undefined;
        });

        hasChanges = true;
      }

      for (const wordInError of wordsInError) {
        const line = currentSubtitle.lines[wordInError.indexLine];
        if (line) {
          const word = line.words.find((x) => x.text === wordInError.text);
          if (word && word.suggest !== wordInError.suggest) {
            word.suggest = wordInError.suggest;
            hasChanges = true;
          }
        }
      }

      if (hasChanges) {
        this.updateSubtitle(currentSubtitle);
      }
    },

    setCountWordsInError(nbWordsInError){
      setNbWordsInError(nbWordsInError);
    },

     async goToSubtitleAndWord(id, timecode) {
      if (!state.active) {
        return;
      }

      const subtitle = displaySubtitles[id];

      const wordFounded = subtitle.lines
        .flatMap((l) => l.words)
        .find((x) => x.in === timecode);

      if (wordFounded) {
        const activeRef = subtitleRefs.current.find(
          (x) => x.subtitle.id === subtitle.id
        );
        if (activeRef) {
          activeRef.onGoToWord(wordFounded.indexWord);
        }
      }
    },

    setCommonErrors(blob) {
      dispatch({
        type: 'setCommonErrors',
        value: {
          commonErrors: blob,
        },
      });
    },

    processShortCutOverride(key, value) {
      const activeRef = subtitleRefs.current.find(
        (x) => x.subtitle.id === state.currentSubtitleId
      );
      if (activeRef) 
        {

         if (value) {
          activeRef.setCurrentPositionOnLetter(value);
        }

        //activeRef.cptCurs = 0;
        activeRef.processShortcutOverride(key);
      }
    },

    processShortcut(key, event) {
      if (!this.active) {
        return;
      }

      console.log('processShortcut', key);

     /* if (displayFilter) {
        
        filterRef.current.onKeyDown(event);

        return;
      }*/

      // si le panneau de recherche est affiché, alors le keydown doit y être appliqué
     /* if (
        state.search.showSearch &&
        event &&
        event.target.id !== 'liststTextDiv'
      ) {
        if (
          searchRef &&
          searchRef.current &&
          key !== 'UNDO' &&
          key !== 'REDO'
        ) {
          searchRef.current.onKeyDown(event);
          return;
        }
      }

      if (key === 'SEARCH' || key === 'REPLACE') {
        if (state.filtered) {
          dispatch({
            type: 'onShowPopup',
            value: {
              title: '',
              text: {
                fr: "Vous devez d'abord effacer les filtres avant de commencer la recherche.",
                en: 'You must first clear the filters before starting the search.',
              },
              buttons: [],
              showIcon: true,
              icon: null,
            },
          });

          return;
        }

        if (key === 'SEARCH') {
          dispatch({ type: 'OnSearchOpenOrClose' });
        } else {
          dispatch({ type: 'OnReplaceOpenOrClose' });
        }

        return;
      }*/

      if (state.readOnly) {
        return;
      }

      if (!state.active && !this.canExecuteShortcutIfSubtitleNotActive(key)) {
        return;
      }

      const { subtitles, subtitlesFiltered, filtered, currentSubtitleId } =
        state;

      let newCurrentIndex = 0;

      switch (key) {
        case 'FIRSTSUBTITLE':
          newCurrentIndex = 0;
          break;

        case 'LASTSUBTITLE':
          newCurrentIndex = subtitles.length - 1;
          break;

        case 'PGUP':
          newCurrentIndex =
            (filtered
              ? subtitlesFiltered.findIndex((x) => x.id === currentSubtitleId)
              : subtitles.findIndex((x) => x.id === currentSubtitleId)) - 1;

          break;

        case 'PGDOWN':
          newCurrentIndex =
            (filtered
              ? subtitlesFiltered.findIndex((x) => x.id === currentSubtitleId)
              : subtitles.findIndex((x) => x.id === currentSubtitleId)) + 1;

          break;

        case 'UNDO':
        case 'REDO':
          onUndoOrRedo(key);
          return;

        case 'REFRESH':
          onRefreshListSubtitlesCanvas();
          return;

        default:
          const activeRef = subtitleRefs.current.find(
            (x) => x.subtitle.id === state.currentSubtitleId
          );
          if (activeRef) {
            //activeRef.cptCurs = 0;
            activeRef.processShortcut(key,event);
          } else {
            // TODO Popup pour dire que le composant n'a pas le focus
          }

          return;
      }

      const newSubtitle = filtered
        ? subtitlesFiltered[newCurrentIndex]
        : subtitles[newCurrentIndex];

      if (newSubtitle) {
        // this.setCurrentSubtitle(newSubtitle, true);
        /*   dispatch({
          type: 'onScroll',
          value: {
            scrolled: true,
            direction:
              newSubtitle.id < state.currentSubtitleId ? 'top' : 'bottom',
          },
        });*/
        props.onMove(newSubtitle.id, newSubtitle.in);
      }
    },

    canExecuteShortcutIfSubtitleNotActive(key) {
      const keys = [
        'TAKEIN',
        'TAKEOUT',
        'TAKETIMECODEVIDEOTOINSUBTITLE',
        'TAKETIMECODEVIDEOTOOUTSUBTITLE',
        'TAKETIMECODEOUTFROMPREVSUBTITLETOINCURRENTSUBTITLE',
        'TAKETIMECODEINFROMNEXTSUBTITLETOOUTCURRENTSUBTITLE',
      ];

      return keys.includes(key);
    },

    addChar(char) {
      if (!state.active || state.readOnly) {
        return;
      }

      const activeRef = subtitleRefs.current.find(
        (x) => x.subtitle.id === state.currentSubtitleId
      );
      if (activeRef) {
        activeRef.addChar(char);
      }
    },

    addHebrewOrArabicChar(char) {
      if (!state.active || state.readOnly) {
        return;
      }

      const activeRef = subtitleRefs.current.find(
        (x) => x.subtitle.id === state.currentSubtitleId
      );
      if (activeRef) {
        activeRef.addHebrewOrArabicChar(char);
      }
    },

    onShowSearch() {
      dispatch({ type: 'OnSearchOpen' });
    },

    onCloseSearch() {
      dispatch({ type: 'OnSearchClose' });
    },

    onShowReplace() {
      dispatch({ type: 'OnReplaceOpenOrClose' });
    },

    setPositionSearch(wordsToSearch, currentPosition, move) {
      onDispatch(
        {
          type: 'onSearchUpdate',
          value: {
            showSelection: move,
            wordsToSearch,
            currentPosition,
          },
        },
        () => {
          subtitleRefs.current.forEach((subtitleRef) => {
            subtitleRef.onRefreshCanvas();
          });

          if (move && state.active) {
            setPositionOfSearch(currentPosition, wordsToSearch);
          }         

        });

    },

    onReplaceAll(text, newText, wholeWord, caseSensitive) {
      onReplaceAll(text, newText, wholeWord, caseSensitive);
    },
  }));

  useEffect(() => {
    console.log('Composant monté');

    const onResize = (width, height) => {
      dispatch({
        type: 'onResize',
        value: {
          width,
          height,
        },
      });

      setForceRedraw(true);
    };

    // on va surveiller automatiquement les changements de taille du foreCanvas......
    const div = document.querySelector('#liststTextDiv'); // Sélectionne la div

    // Crée un ResizeObserver
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        onResize(width, height);
      }
    });

    // Observer les changements de taille de la div du foreCanvas
    resizeObserver.observe(div);

    return () => {
      console.log('Composant démonté');
      resizeObserver.disconnect(div);
    };
  }, []);

  const createRefForInstance = useCallback((ref) => {
    if (ref) {
      
       const index = subtitleRefs.current.findIndex(
        (x) => x.subtitle.id === ref.subtitle.id
      );
      if (index !== -1) {
        subtitleRefs.current[index] = ref;

        return;
      }
      
      /*if (subtitleRefs.current.includes(ref)) {
        return;
      }*/
      
      subtitleRefs.current.push(ref);
    }
  }, []);

  const onDraw = useCallback(() => {

    // si subtitleNumber est -1, on n'affiche rien, juste un canvas transparent
    if (state.subtitleNumber === -1) {
      setForceRedraw(false);
      return;
    }

    // Départ des coordonnées
    let currentY = 35; // taille de la div listst-title-div

    const subtitles = state.filtered ? state.subtitlesFiltered : state.subtitles;

    if (!subtitles.length) {
      setForceRedraw(false);
      setDisplaySubtitles([]);
      subtitleRefs.current = [];
      return;
    }    

    let { value: scrollValue, hasChanged } = state.scroll;

    if (!hasChanged) {
      const currentIndex = subtitles.findIndex(
        (x) => x.id === state.currentSubtitleId
      );

      if (currentIndex !== -1) {
        let newScrollValue = getNewScrollValue(currentIndex);

        if (newScrollValue !== scrollValue) {
          dispatch({ type: 'onChangeScrollValue', value: newScrollValue });
          setTimeout(() => setForceRedraw(true), 100);
          return;
        }
      }
    } else {
      dispatch({ type: 'onScrollHasChanged' });
    }

    let indexSubtitle = scrollValue;

    subtitles
      .filter((subtitle) => subtitle.display === true)
      .forEach((subtitle) => {
        subtitle.startY = 0;
        subtitle.endY = 0;
        subtitle.display = false;
        //subtitle.focus = false;
      });

    while (currentY < state.height && indexSubtitle < subtitles.length) {
      const subtitle = subtitles[indexSubtitle];
      subtitle.startY = currentY;
      subtitle.endY = currentY + state.lineHeight;
      subtitle.display = true;
     // subtitle.focus = state.currentSubtitleId === subtitle.id;

      currentY += state.lineHeight;
      indexSubtitle++;
    }

    setDisplaySubtitles(subtitles);

    subtitleRefs.current = [
      ...subtitleRefs.current.filter((x) => x.props.subtitle.display === true),
    ];

    subtitleRefs.current.forEach((ref) => {
      ref.createOrUpdateProps();
    });

    setForceRedraw(false);
  }, [getNewScrollValue, state]);

  const onShowPopup = useCallback((value) => {
    
    if (props.applicationsparams.subtitling.disablePopup) return;
    dispatch({
      type: 'onShowPopup',
      value,
    });
  }, []);

  useEffect(() => {
    if (forceRedraw) {
      onDraw();
    }
  }, [forceRedraw, onDraw]);

  useEffect(() => {
    setForceRedraw(true);
  }, [
  //  state.scroll.value,
    state.currentSubtitleId,
    state.subtitles,
    state.filtered,
    state.subtitlesFiltered,
  ]);

  useEffect(() => {
    if (state.readOnly !== props.readOnly) {
      dispatch({ type: 'onUpdateReadOnly', value: props.readOnly });
    }
  }, [props.readOnly, dispatch, state]);

  useEffect(() => {
    if (props.subtitleParameters) {
      dispatch({ type: 'onChangeParams', value: props.subtitleParameters });
      setForceRedraw(true);
    }
  }, [props.subtitleParameters, dispatch]);

  useEffect(() => {
    if (state.filtered) {
      if (state.subtitlesFiltered.length) {
        const firstSub = first(state.subtitlesFiltered);
        props.onMove(firstSub.id, firstSub.in);
      } else {
        props.onMove(-1, null);
      }
    }
    subtitleRefs.current.forEach((sub) => {
      sub.onRefreshCanvas();
    });
  }, [state.filtered, state.subtitlesFiltered]);

  return (
    <div className="listst-full-div" onWheel={onWheel}>
      <div className="listst-header-div">
        <div className={'checkbox' + (state.readOnly ? ' hide' : '')}>
          <label htmlFor="chkAllSubtitles" className="sr-only">
            Sélectionner tous les sous-titres
          </label>
          <input
            type="checkbox"
            id="chkAllSubtitles"
            onChange={(e) => onSelectSubtitles(e.target.checked)}
          ></input>
        </div>
        <div className="error">
          {nbWordsInError > 0
            ? `${nbWordsInError} ${
                lang === 'fr' ? `${nbWordsInError === 1 ? 'mot' : 'mots'} en erreur` 
                : `${nbWordsInError === 1 ? 'word' : 'words'} in error`
              }`
            : ''}
        </div>
        <div className="filter">
          <Filter
            ref={filterRef}
            lang={lang}
            readOnly={state.readOnly}
            onDisplayFilter={(displayed) => setDisplayFilter(displayed)}
            onSetColor={onSetColor}
            onSelectRangeSubtitles={onSelectRangeSubtitles}
            onDeleteSelectedSubtitles={onDeleteSelectedSubtitles}
            onDuplicateSubtitles={onDuplicateSubtitles}
            onSelectColor={onSelectColor}
            onClearFilteredSubtitles={onClearFilteredSubtitles}
            onSelectAlign={onSelectAlign}
            onSelectSubtitles={onSelectSubtitles}
            onSearchWordOnSubtitles={onSearchWordOnSubtitles}
            onCutAndPasteSubtitles={onCutAndPasteSubtitles}
          ></Filter>
        </div>
      </div>
      <div className="listst-top-div">
        <div className="listst-text-div" id="liststTextDiv">
          {displaySubtitles
            .filter((x) => x.display === true)
            .map((item) => {
              return (
                <div className="listst-card-div" key={item.id}>
                  <Subtitle
                    {...props}
                    {...state}
                    key={"Subtitle_" + item.id}
                    subtitle={item}
                    ref={createRefForInstance}
                    onChangePosition={onChangePosition}
                    onSubtitleChecked={onSubtitleChecked}
                    onSubtitleLineChecked={onSubtitleLineChecked}
                    onShowPopup={onShowPopup}
                    onClosePopup={() => dispatch({ type: 'onClosePopup' })}
                    onUpdateAppearenceSpeakers={
                      props.onUpdateAppearenceSpeakers
                    }
                  ></Subtitle>
                </div>
              );
            })}
        </div>
        <div className="listst-scroll-div">
          <input
            type="range"
            min="0"
            max={displaySubtitles.length - state.nbVisibleLines}
            value={state.scroll.value}
            orient="vertical"
            onChange={onHandleChangeScroll}
          />
        </div>
      </div>

      <SimplePopup
        display={state.popup.display}
        title={state.popup.title}
        text={state.popup.text}
        onCancel={() =>
          state.popup.onCancel
            ? state.popup.onCancel()
            : dispatch({ type: 'onClosePopup' })
        }
        buttons={state.popup.buttons}
        showIcon={state.popup.showIcon}
        icon={state.popup.icon}
      />
    </div>
  );
});

export default EditListSt;
